import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Icon, PrimaryButton, Stack, Text } from "@fluentui/react";
import React from "react";
import { ElxTagPicker, InputMessageTypes, TagContainerOrientation } from "@elixir/components";
import { isValidSupportcase } from "../../../library/utilities/FieldValidation";
import { IncidentTiles } from "../IncidentTiles";
import { SupportTicketTiles } from "../SupportTicketTiles";
import { callTypeRequiresIncident, callTypeRequiresSupportRequest, } from "../ConfidenceCallTypeConstants";
import { recentOutagesSectionStyles } from "../styles/RecentOutagesSection.styles";
import { containerStyles, tileStyles } from "../styles/shared.styles";
export const RecentOutagesSection = ({ isExpanded, setIsExpanded, isUnlocked, setHasManuallyReopened, isManualCustomerEntry, validCustomerTpid, validCustomerName, incidentCount, customerTpid, selectedCallType, showOutagesSection, urlOutageIds, selectedIncidents, setSelectedIncidents, selectedSupportRequestNumbers, setSelectedSupportRequestNumbers, setIncidentCount, onNext, isSubscriptionComplete = true, onIncidentsLoaded, onSupportTicketsLoaded, children, }) => {
    const [validManualIncidentNumbers, setValidManualIncidentNumbers] = React.useState(undefined);
    const [validManualSupportRequestNumbers, setValidManualSupportRequestNumbers] = React.useState(undefined);
    const [manualIncidentFormatError, setManualIncidentFormatError] = React.useState(false);
    const [manualSupportRequestFormatError, setManualSupportRequestFormatError] = React.useState(false);
    // Total number of support tickets loaded for the customer, surfaced in the
    // section header alongside the outage/issue count.
    const [supportTicketCount, setSupportTicketCount] = React.useState(0);
    const [incidentApiResult, setIncidentApiResult] = React.useState(undefined);
    const previousSelectedCallTypeRef = React.useRef(selectedCallType);
    React.useEffect(() => {
        const previousSelectedCallType = previousSelectedCallTypeRef.current;
        previousSelectedCallTypeRef.current = selectedCallType;
        if (previousSelectedCallType &&
            previousSelectedCallType !== selectedCallType) {
            setValidManualIncidentNumbers(undefined);
            setValidManualSupportRequestNumbers(undefined);
            setManualIncidentFormatError(false);
            setManualSupportRequestFormatError(false);
        }
    }, [selectedCallType]);
    const handleIncidentCountChange = React.useCallback((count) => {
        setIncidentCount(count);
    }, [setIncidentCount]);
    const handleIncidentsLoaded = React.useCallback((incidents) => {
        onIncidentsLoaded === null || onIncidentsLoaded === void 0 ? void 0 : onIncidentsLoaded(incidents);
    }, [onIncidentsLoaded]);
    const handleCustomerIncidentLookupCompleted = React.useCallback((hasResults) => {
        setIncidentApiResult({
            customerTpid,
            hasResults,
        });
    }, [customerTpid]);
    const [supportApiResult, setSupportApiResult] = React.useState(undefined);
    const handleSupportTicketCountChange = React.useCallback((count) => {
        setSupportApiResult((previousResult) => ({
            customerTpid,
            hasResults: ((previousResult === null || previousResult === void 0 ? void 0 : previousResult.customerTpid) === customerTpid &&
                previousResult.hasResults) ||
                count > 0,
        }));
        setSupportTicketCount(count);
    }, [customerTpid]);
    const incidentTags = selectedIncidents.map((id) => ({
        key: id,
        text: id,
    }));
    const supportRequestTags = selectedSupportRequestNumbers.map((id) => ({
        key: id,
        text: id,
    }));
    const addManualIncidentTag = (tag) => {
        var _a, _b;
        const value = (_b = (_a = tag.text) === null || _a === void 0 ? void 0 : _a.trim()) !== null && _b !== void 0 ? _b : "";
        const isValid = value.length >= 2 && isValidSupportcase(value);
        if (isValid &&
            value &&
            selectedIncidents.findIndex((item) => item === value) === -1) {
            setSelectedIncidents((prev) => [...prev, value]);
            setIncidentCount((count) => count + 1);
            setValidManualIncidentNumbers(true);
            setManualIncidentFormatError(false);
        }
        else if (!isValid) {
            setManualIncidentFormatError(true);
        }
    };
    const addManualSupportRequestTag = (tag) => {
        var _a, _b;
        const value = (_b = (_a = tag.text) === null || _a === void 0 ? void 0 : _a.trim()) !== null && _b !== void 0 ? _b : "";
        const isValid = value.length >= 2 && isValidSupportcase(value);
        if (isValid &&
            value &&
            !selectedSupportRequestNumbers.includes(value)) {
            setSelectedSupportRequestNumbers((prev) => [...prev, value]);
            setValidManualSupportRequestNumbers(true);
            setManualSupportRequestFormatError(false);
        }
        else if (!isValid) {
            setManualSupportRequestFormatError(true);
        }
    };
    const removeManualSupportRequestTag = (tag) => {
        const nextSupportRequests = selectedSupportRequestNumbers.filter((item) => item !== tag.text);
        setSelectedSupportRequestNumbers(nextSupportRequests);
        setValidManualSupportRequestNumbers(nextSupportRequests.length > 0 ? true : undefined);
    };
    const removeManualIncidentTag = (tag) => {
        const nextIncidentIds = selectedIncidents.filter((item) => item !== tag.text);
        setSelectedIncidents(nextIncidentIds);
        setIncidentCount(nextIncidentIds.length);
        setValidManualIncidentNumbers(nextIncidentIds.length > 0 ? true : undefined);
    };
    const handleToggleExpanded = () => {
        if (!isUnlocked)
            return;
        const newExpandedState = !isExpanded;
        setIsExpanded(newExpandedState);
        // If section is being manually opened, mark it as manually reopened
        if (newExpandedState) {
            setHasManuallyReopened(true);
        }
    };
    const handleKeyDown = (e) => {
        if (!isUnlocked)
            return;
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleToggleExpanded();
        }
    };
    const handleOutageTileClick = (outageFullId) => {
        if (selectedIncidents.includes(outageFullId)) {
            setSelectedIncidents((prev) => prev.filter((id) => id !== outageFullId));
        }
        else {
            setSelectedIncidents((prev) => [...prev, outageFullId]);
        }
    };
    const handleOutageTileKeyDown = (e, outageFullId) => {
        if (e.key === "Enter" || e.key === " ") {
            handleOutageTileClick(outageFullId);
        }
    };
    // The same call-type configuration drives both tile badges and data-entry
    // requirements.
    const requiresIncident = callTypeRequiresIncident(selectedCallType);
    const requiresSupportRequest = callTypeRequiresSupportRequest(selectedCallType);
    const requirementsMet = (!requiresIncident || selectedIncidents.length > 0) &&
        (!requiresSupportRequest || selectedSupportRequestNumbers.length > 0) &&
        isSubscriptionComplete;
    const isNumericCustomerTpid = /^\d+$/.test(customerTpid.trim());
    const incidentApiLoadDone = (incidentApiResult === null || incidentApiResult === void 0 ? void 0 : incidentApiResult.customerTpid) === customerTpid;
    const showIncidentTiles = isNumericCustomerTpid &&
        (!incidentApiLoadDone || incidentApiResult.hasResults);
    const supportApiLoadDone = (supportApiResult === null || supportApiResult === void 0 ? void 0 : supportApiResult.customerTpid) === customerTpid;
    const showSupportTicketTiles = isNumericCustomerTpid &&
        (!supportApiLoadDone || supportApiResult.hasResults);
    return (_jsxs(Stack, Object.assign({ tokens: { childrenGap: 16 }, style: {
            marginTop: "24px",
            opacity: isUnlocked ? 1 : 0.6,
        } }, { children: [_jsxs("div", Object.assign({ className: requirementsMet
                    ? recentOutagesSectionStyles.sectionHeaderComplete
                    : recentOutagesSectionStyles.sectionHeader, onClick: handleToggleExpanded, role: "button", "aria-expanded": isExpanded, "aria-disabled": !isUnlocked, "data-testid": "recent-issues-section-header", tabIndex: isUnlocked ? 0 : -1, style: {
                    cursor: isUnlocked ? "pointer" : "not-allowed",
                }, onKeyDown: handleKeyDown }, { children: [_jsx(Icon, { iconName: isExpanded && isUnlocked ? "ChevronDown" : "ChevronRight", style: { fontSize: "12px" } }), requirementsMet && (_jsx(Icon, { iconName: "SkypeCircleCheck", style: { fontSize: "16px", color: "#107c10" } })), _jsxs(Text, Object.assign({ variant: "medium", style: { fontWeight: 600 } }, { children: ["Recent Issues Found", (validCustomerTpid === true || validCustomerName === true) && (_jsxs("span", { children: [" | Found " + incidentCount + " issue", incidentCount !== 1 ? "s" : "", " for Customer TPID: " + customerTpid] })), selectedIncidents.length > 0 && (_jsx("span", { children: " | " + selectedIncidents.length + " of " + incidentCount + " selected" })), (validCustomerTpid === true || validCustomerName === true) &&
                                supportTicketCount > 0 && (_jsxs("span", { children: [" | " + supportTicketCount + " support request", supportTicketCount !== 1 ? "s" : "", " found"] })), selectedSupportRequestNumbers.length > 0 && (_jsx("span", { children: " | " +
                                    selectedSupportRequestNumbers.length +
                                    " of " +
                                    supportTicketCount +
                                    " support requests selected" }))] }))] })), isExpanded && isUnlocked && showOutagesSection && (_jsxs("div", Object.assign({ className: recentOutagesSectionStyles.incidentTilesContainer }, { children: [_jsxs(Text, Object.assign({ variant: "medium", className: recentOutagesSectionStyles.fieldLabel }, { children: ["Incident or Outage Details (IcM)", " ", requiresIncident ? (_jsx("span", Object.assign({ className: recentOutagesSectionStyles.requiredIndicator }, { children: "*" }))) : (_jsx("span", Object.assign({ className: recentOutagesSectionStyles.optionalIndicator }, { children: "(optional)" })))] })), !isManualCustomerEntry && urlOutageIds.length > 0 ? (
                    // Show custom tiles for URL-provided outage IDs
                    _jsx("div", Object.assign({ className: containerStyles.tilesContainer }, { children: urlOutageIds.map((outage, index) => (_jsxs("div", Object.assign({ className: tileStyles.unifiedTile(selectedIncidents.includes(outage.fullId)), onClick: () => handleOutageTileClick(outage.fullId), role: "button", tabIndex: 0, onKeyDown: (e) => handleOutageTileKeyDown(e, outage.fullId), "data-testid": `url-outage-tile-${outage.fullId}` }, { children: [_jsxs("div", Object.assign({ className: tileStyles.tileHeader }, { children: [_jsx(Icon, { iconName: "Warning", className: tileStyles.tileIcon(selectedIncidents.includes(outage.fullId)) }), _jsxs(Text, Object.assign({ className: tileStyles.tileTitle(selectedIncidents.includes(outage.fullId)) }, { children: [outage.type, ": ", outage.id] }))] })), selectedIncidents.includes(outage.fullId) && (_jsx(Icon, { iconName: "CheckMark", style: {
                                        position: "absolute",
                                        top: "50%",
                                        right: "12px",
                                        transform: "translateY(-50%)",
                                        fontSize: "14px",
                                        color: "#0078d4",
                                    } }))] }), `url-outage-${index}`))) }))) : showIncidentTiles ? (_jsx(Stack, Object.assign({ tokens: { childrenGap: 12 } }, { children: _jsx(IncidentTiles, { customerTpid: customerTpid, isVisible: true, selectedIncidents: selectedIncidents, onSelectionChange: setSelectedIncidents, onIncidentCountChange: handleIncidentCountChange, onIncidentsLoaded: handleIncidentsLoaded, onCustomerLookupCompleted: handleCustomerIncidentLookupCompleted }) }))) : (
                    // A successful lookup returned no results, or no numeric TPID is
                    // available, so allow the user to provide an IcM reference.
                    _jsx(Stack, Object.assign({ tokens: { childrenGap: 12 } }, { children: _jsx(Stack, Object.assign({ tokens: { childrenGap: 4 } }, { children: _jsx(ElxTagPicker, { title: "Incident or Outage Details", placeholder: "Add IcM incident or outage reference", items: incidentTags, orientation: TagContainerOrientation.Horizontal, onAddTag: addManualIncidentTag, onRemoveTag: removeManualIncidentTag, onBlur: (ev) => {
                                    var _a, _b;
                                    const pendingValue = (_b = (_a = ev.target.value) === null || _a === void 0 ? void 0 : _a.trim()) !== null && _b !== void 0 ? _b : "";
                                    // Do not force invalid on blur while user-entered tag text is
                                    // transitioning into a chip; addTag handles invalid content.
                                    if (pendingValue.length > 0) {
                                        return;
                                    }
                                    // Defer required-field validation to allow tag state updates
                                    // to settle, which prevents transient error flicker. When an
                                    // incident is not required for the selected call type, never
                                    // flag the empty field as invalid.
                                    setTimeout(() => {
                                        setValidManualIncidentNumbers(!requiresIncident || selectedIncidents.length > 0
                                            ? true
                                            : false);
                                    }, 0);
                                }, message: manualIncidentFormatError ||
                                    (validManualIncidentNumbers === false && requiresIncident)
                                    ? {
                                        type: InputMessageTypes.Error,
                                        content: "Add at least one valid incident/outage reference (letters, numbers, hyphen).",
                                    }
                                    : undefined }) })) }))), _jsxs(Stack, Object.assign({ tokens: { childrenGap: 8 }, className: recentOutagesSectionStyles.supportRequestsContainer, "data-testid": "support-requests-subsection" }, { children: [_jsxs(Text, Object.assign({ variant: "medium", className: recentOutagesSectionStyles.fieldLabel }, { children: ["Support Requests", requiresSupportRequest ? (_jsxs("span", Object.assign({ className: recentOutagesSectionStyles.requiredIndicator }, { children: [" ", "*"] }))) : (_jsxs("span", Object.assign({ className: recentOutagesSectionStyles.optionalIndicator }, { children: [" ", "(optional)"] }))), selectedSupportRequestNumbers.length > 0 && (_jsx("span", Object.assign({ className: recentOutagesSectionStyles.optionalIndicator }, { children: " | " + selectedSupportRequestNumbers.length + " selected" })))] })), showSupportTicketTiles ? (_jsx(SupportTicketTiles, { customerTpid: customerTpid, isVisible: true, selectedTickets: selectedSupportRequestNumbers, onSelectionChange: setSelectedSupportRequestNumbers, onTicketCountChange: handleSupportTicketCountChange, onTicketsLoaded: onSupportTicketsLoaded })) : (_jsx(ElxTagPicker, { title: "Support Requests", placeholder: "Add support request number", items: supportRequestTags, orientation: TagContainerOrientation.Horizontal, onAddTag: addManualSupportRequestTag, onRemoveTag: removeManualSupportRequestTag, onBlur: (ev) => {
                                    var _a, _b;
                                    const pendingValue = (_b = (_a = ev.target.value) === null || _a === void 0 ? void 0 : _a.trim()) !== null && _b !== void 0 ? _b : "";
                                    if (pendingValue.length > 0) {
                                        return;
                                    }
                                    setTimeout(() => {
                                        setValidManualSupportRequestNumbers(!requiresSupportRequest ||
                                            selectedSupportRequestNumbers.length > 0
                                            ? true
                                            : false);
                                    }, 0);
                                }, message: manualSupportRequestFormatError ||
                                    (validManualSupportRequestNumbers === false &&
                                        requiresSupportRequest)
                                    ? {
                                        type: InputMessageTypes.Error,
                                        content: "Add at least one valid support request number.",
                                    }
                                    : undefined }))] })), children, _jsx("div", Object.assign({ className: recentOutagesSectionStyles.actionsContainer }, { children: _jsx(PrimaryButton, { text: "Continue", onClick: onNext, disabled: !onNext || !requirementsMet, "data-testid": "issues-continue", styles: {
                                root: {
                                    minWidth: "120px",
                                    height: "32px",
                                },
                            } }) }))] })))] })));
};
